import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import toast, { Toaster } from 'react-hot-toast';
import {
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    TextField,
    Tooltip,
} from '@mui/material';
import userImg from "../../assets/images/user.svg";
import { fetchUsers, exportUsers } from '../../Redux/Reducers/UserManagementReducer';
import { useDispatch, useSelector } from 'react-redux';
import { usePermissions } from '../../context/AuthContext';

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

function UsersList() {
    const { can } = usePermissions();

    const [selectedRows, setSelectedRows] = useState([]);
    const [showInactive, setShowInactive] = useState(false);
    const [inactiveUsers, setInactiveUsers] = useState([]);
    const [inactiveUsersLoading, setInactiveUsersLoading] = useState(false);
    const [inactiveRowCount, setInactiveRowCount] = useState(0);

    const [searchValue, setSearchValue] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const debouncedSearchValue = useDebounce(searchValue, 500);
    const debouncedStartDate = useDebounce(startDate, 500);
    const debouncedEndDate = useDebounce(endDate, 500);

    const [couponSubject, setCouponSubject] = useState('');
    const [couponMessage, setCouponMessage] = useState('');
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedUserIdToDelete, setSelectedUserIdToDelete] = useState(null);
    const [couponOpen, setCouponOpen] = useState(false);
    const [couponValue, setCouponValue] = useState('');
    const [submitAttemptedCoupon, setSubmitAttemptedCoupon] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const usersFromStore = useSelector((state) => state?.UserManagementReducer?.users || []);
    const paginationInfo = useSelector((state) => state?.UserManagementReducer?.pagination);
    const loading = useSelector((state) => state?.UserManagementReducer?.loading || false);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

    const isInitialMount = useRef(true);

    const fetchInactiveUsers = useCallback(async (page, limit, search = '') => {
        setInactiveUsersLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/inactiveUsers`, {
                headers: { 'Authorization': `${localStorage.getItem("vedhvidhi-admin-token")}` },
                params: {
                    page,
                    limit,
                    search,
                }
            });

            if (response.status === 200 && response.data.data && typeof response.data.data !== 'undefined') {
                setInactiveUsers(response.data.data || []);
                setInactiveRowCount(response.data.paginationDetail?.totalDocs || 0);
            } else {
                setInactiveUsers([]);
                setInactiveRowCount(0);
            }
        } catch (error) {
            console.error('Error fetching inactive users:', error);
            setInactiveUsers([]);
            setInactiveRowCount(0);
            toast.error(error.response?.data?.message || 'Failed to fetch inactive users.');
        } finally {
            setInactiveUsersLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, [debouncedSearchValue]);

    useEffect(() => {
        const { page, pageSize } = paginationModel;
        if (showInactive) {
            fetchInactiveUsers(page + 1, pageSize, debouncedSearchValue);
        } else {
            dispatch(fetchUsers({ page: page + 1, limit: pageSize, search: debouncedSearchValue, startDate: debouncedStartDate, endDate: debouncedEndDate }));
        }
    }, [paginationModel, dispatch, showInactive, debouncedSearchValue, debouncedStartDate, debouncedEndDate, fetchInactiveUsers]);


    const handleManageSelected = () => {
        setSearchValue('');
        setShowInactive(true);
        setSelectedRows([]);
        setPaginationModel(prev => ({ page: 0, pageSize: prev.pageSize }));
    };

    const handleShowAllUsers = () => {
        setSearchValue('');
        setShowInactive(false);
        setSelectedRows([]);
        setPaginationModel(prev => ({ page: 0, pageSize: prev.pageSize }));
    };

    const handlePaginationModelChange = (newModel) => {
        setPaginationModel(newModel);
    };

    const handleCouponClick = () => {
        if (selectedRows.length === 0) {
            toast.error("Please select users to send a coupon.");
            return;
        }
        setCouponOpen(true);
    };

    const handleCouponClose = () => {
        setCouponOpen(false);
        setCouponValue('');
        setCouponSubject('');
        setCouponMessage('');
        setSubmitAttemptedCoupon(false);
    };

    const handleCouponSend = async () => {
        setSubmitAttemptedCoupon(true);
        if (selectedRows.length === 0 || !couponValue.trim() || !couponSubject.trim() || !couponMessage.trim()) {
            toast.error("Please fill all fields and select users.");
            return;
        }
        try {
            const response = await axios.post(`${process.env.REACT_APP_BASEURL}/admin/userGiftAmount`, {
                amount: parseFloat(couponValue),
                subject: couponSubject,
                message: couponMessage,
                userIds: selectedRows,
            }, {
                headers: {
                    'Authorization': `${localStorage.getItem("vedhvidhi-admin-token")}`,
                    'Content-Type': 'application/json',
                },
            });
            if (response.status === 200) {
                toast.success("Coupon sent successfully!");
                handleCouponClose();
                setSelectedRows([]);
            } else {
                toast.error(response.data?.message || "Failed to send coupon.");
            }
        } catch (error) {
            console.error('Error sending coupon:', error);
            toast.error(error.response?.data?.message || "Failed to send the coupon. Please try again.");
        }
    };

    const handleExport = async () => {
        try {
            const result = await dispatch(exportUsers({ search: debouncedSearchValue, startDate: debouncedStartDate, endDate: debouncedEndDate })).unwrap();
            if (result?.data && result.data.length > 0) {
                const headers = ['S.No', 'Name', 'Email', 'Phone', 'Created At'];
                const csvContent = [
                    headers.join(','),
                    ...result.data.map((user, idx) => [
                        idx + 1,
                        `"${(user.fullName || '').replace(/\"/g, '"')}"`,
                        user.email || '',
                        user.mobileNumber || '',
                        user.createdAt ? new Date(user.createdAt).toLocaleString() : ''
                    ].join(','))
                ].join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `users_${new Date().getTime()}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success('Users exported successfully!');
            } else {
                toast('No data to export');
            }
        } catch (err) {
            console.error('Export error:', err);
            toast.error('Failed to export users');
        }
    };

    const handleClearFilters = () => {
        setSearchValue('');
        setStartDate('');
        setEndDate('');
        // reset to first page
        setPaginationModel(prev => ({ ...prev, page: 0 }));

        const { pageSize } = paginationModel;
        if (showInactive) {
            fetchInactiveUsers(1, pageSize, '');
        } else {
            dispatch(fetchUsers({ page: 1, limit: pageSize, search: '', startDate: '', endDate: '' }));
        }
    };

    const handleDeleteUserClick = (userId) => {
        setSelectedUserIdToDelete(userId);
        setOpenDeleteDialog(true);
    };

    const handleDeleteDialogClose = () => {
        setOpenDeleteDialog(false);
        setSelectedUserIdToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedUserIdToDelete) return;
        try {
            const response = await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/user/${selectedUserIdToDelete}`, {
                headers: { 'Authorization': ` ${localStorage.getItem("vedhvidhi-admin-token")}` },
            });
            if (response.status === 200) {
                toast.success("User deleted successfully!");
                setSelectedRows(prev => prev.filter(id => id !== selectedUserIdToDelete));
                const { page, pageSize } = paginationModel;
                if (showInactive) {
                    fetchInactiveUsers(page + 1, pageSize, debouncedSearchValue);
                } else {
                    dispatch(fetchUsers({ page: page + 1, limit: pageSize, search: debouncedSearchValue, startDate: debouncedStartDate, endDate: debouncedEndDate }));
                }
            } else {
                toast.error(response.data?.message || "Failed to delete user.");
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error(error.response?.data?.message || "Failed to delete the user. Please try again.");
        } finally {
            handleDeleteDialogClose();
        }
    };

    const actionField = can('users:edit') || can('users:delete') ? [{
        field: "actions", headerName: "Actions", width: 100, sortable: false, filterable: false,
        renderCell: ({ row }) => (
            <Box>
                {can('users:edit') && (
                    <Tooltip title="Edit User">
                        <IconButton component={Link} to={`/users-edit/${row._id}`} color="primary">
                            <i className="icofont-edit text-info"></i>
                        </IconButton>
                    </Tooltip>
                )}
                {can('users:delete') && (
                    <Tooltip title="Delete User">
                        <IconButton color="error" onClick={() => handleDeleteUserClick(row._id)}>
                            <i className="icofont-ui-delete text-danger"></i>
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        ),
    }] : []

    const columns = [
        { field: 'sono', headerName: 'S.No', width: 70, sortable: false, filterable: false },
        {
            field: 'profileImg', headerName: 'Image', width: 80,
            renderCell: (params) => (<img src={params.value || userImg} alt="User" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />),
            sortable: false, filterable: false,
        },
        {
            field: 'fullName', headerName: 'Name', width: 200,
            renderCell: (params) => (
                <Button variant="text" size="small" sx={{ textTransform: 'none', p: 0, justifyContent: 'flex-start' }} onClick={() => navigate(`/booking-by-user/${params.row._id}`)}>
                    {params.value}
                </Button>
            ),
        },
        { field: 'email', headerName: 'Email', width: 250 },
        { field: 'mobileNumber', headerName: 'Mobile', width: 130 },
        { field: 'createdAt', headerName: 'Created At', width: 170 },
        { field: 'updatedAt', headerName: 'Updated At', width: 170 },
        , ...actionField
    ];

    const baseDataSet = showInactive ? inactiveUsers : usersFromStore;

    const currentGridRows = (baseDataSet || []).map((user, index) => {
        const serialNumberBase = paginationModel.page * paginationModel.pageSize;
        return {
            id: user._id,
            _id: user._id,
            sono: serialNumberBase + index + 1,
            profileImg: user.profileImg || userImg,
            fullName: user.fullName || 'N/A',
            email: user.email || 'N/A',
            mobileNumber: user.mobileNumber || 'N/A',
            createdAt: user.createdAt ? new Date(user.createdAt).toLocaleString() : "",
            updatedAt: user.updatedAt ? new Date(user.updatedAt).toLocaleString() : "",
        };
    });

    return (
        <>
            <Toaster />
            <div className="body d-flex py-1">
                <div className="container-xxl">
                    <PageHeader1
                        righttitle="Add Users"
                        link="/users-add"
                        routebutton={can('users:add')}
                        pagetitle='User Management List'
                    />
                    <div className="row g-0 mb-3">
                        <div className="col-md-12">
                            <div className="card">
                                <div className="card-body">
                                    <Box sx={{ width: '100%' }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap">
                                            <Box display="flex" gap={2} alignItems="center" mb={2}>
                                                <Button variant="contained" onClick={showInactive ? handleShowAllUsers : handleManageSelected}>
                                                    {showInactive ? "Show All Users" : "Inactive Users"}
                                                </Button>
                                                {can('users:edit') && (
                                                    <Button variant="contained" color="primary" onClick={handleCouponClick} disabled={selectedRows.length === 0}>
                                                        Send Gift Amount
                                                    </Button>
                                                )}
                                            </Box>

                                            <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
                                                <TextField
                                                    placeholder="Search by name, email, phone..."
                                                    value={searchValue}
                                                    onChange={(e) => setSearchValue(e.target.value)}
                                                    size="small"
                                                    sx={{ width: { xs: '100%', sm: '250px' } }}
                                                />
                                                <TextField
                                                    type="date"
                                                    label="Start Date"
                                                    value={startDate}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    size="small"
                                                    sx={{ width: '180px' }}
                                                />
                                                <TextField
                                                    type="date"
                                                    label="End Date"
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    size="small"
                                                    sx={{ width: '180px' }}
                                                />
                                                <Button variant="contained" color="success" onClick={handleExport} sx={{ height: '40px' }}>
                                                    Export
                                                </Button>
                                                <Button variant="outlined" color="secondary" onClick={handleClearFilters} sx={{ height: '40px' }}>
                                                    Clear
                                                </Button>
                                            </Box>
                                        </Box>
                                        <DataGrid
                                            rows={currentGridRows}
                                            columns={columns}
                                            getRowId={(row) => row.id}
                                            autoHeight
                                            loading={showInactive ? inactiveUsersLoading : loading}
                                            checkboxSelection
                                            rowSelectionModel={selectedRows}
                                            onRowSelectionModelChange={(newSelectionModel) => setSelectedRows(newSelectionModel)}
                                            keepNonExistentRowsSelected
                                            paginationMode="server"
                                            rowCount={showInactive ? inactiveRowCount : (paginationInfo?.totalDocs || 0)}
                                            pageSizeOptions={[10, 25, 50, 100]}
                                            paginationModel={paginationModel}
                                            onPaginationModelChange={handlePaginationModelChange}
                                            columnVisibilityModel={columnVisibilityModel}
                                            onColumnVisibilityModelChange={setColumnVisibilityModel}
                                            disableRowSelectionOnClick
                                        />
                                    </Box>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Dialog open={couponOpen} onClose={handleCouponClose} fullWidth maxWidth="sm">
                    <DialogTitle>Send Coupon</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            Enter coupon details for {selectedRows.length} selected user(s).
                        </DialogContentText>
                        <TextField autoFocus margin="dense" label="Amount (₹)" type="number" fullWidth variant="outlined" value={couponValue}
                            onChange={(e) => setCouponValue(e.target.value)} error={submitAttemptedCoupon && !couponValue.trim()}
                            helperText={submitAttemptedCoupon && !couponValue.trim() ? 'Amount is required' : ''} inputProps={{ min: "0" }} />
                        <TextField margin="dense" label="Subject" fullWidth variant="outlined" value={couponSubject}
                            onChange={(e) => setCouponSubject(e.target.value)} error={submitAttemptedCoupon && !couponSubject.trim()}
                            helperText={submitAttemptedCoupon && !couponSubject.trim() ? 'Subject is required' : ''} />
                        <TextField margin="dense" label="Message" fullWidth multiline rows={4} variant="outlined" value={couponMessage}
                            onChange={(e) => setCouponMessage(e.target.value)} error={submitAttemptedCoupon && !couponMessage.trim()}
                            helperText={submitAttemptedCoupon && !couponMessage.trim() ? 'Message is required' : ''} />
                    </DialogContent>
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={handleCouponClose}>Cancel</Button>
                        <Button onClick={handleCouponSend} variant="contained" color="primary">Send</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={openDeleteDialog} onClose={handleDeleteDialogClose}>
                    <DialogTitle>{"Delete Confirmation"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this user record? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteDialogClose}>Cancel</Button>
                        <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>Confirm Delete</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </>
    );
}

export default UsersList;